// Kaun Banega Cables-pati — main game component.
//
// Stage layout (mobile-first, scales up on desktop):
//   • Top bar: timer ring + lifelines + walk-away + points display
//   • Center: question card + 4 diagonal answer panels
//   • Right (desktop only) or below (mobile): prize ladder
//
// Game flow per question:
//   ready → pick → lock confirm → reveal → explanation → next / end
//
// Lifelines:
//   50:50          — hides 2 wrong options (instant, no modal)
//   audience_poll  — modal with bar chart
//   phone_a_friend — modal with friend hint (typewriter)
//   expert_advice  — modal with senior engineer insight (typewriter)
//
// Win = clear all 16. Wrong = drop to last safe milestone. Walk-away = take current locked amount.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
    LADDER, JACKPOT_INDEX, SAFE_INDICES, QUESTION_SECONDS, TOPIC_PRESETS, LIFELINES,
    OPTION_LETTERS, fallbackPointsOnWrong, pickFiftyFiftyHidden, FALLBACK_BANK,
    SPEED_TIERS, speedMultiplier, speedTier,
} from './kbc/kbcEngine';
import PrizeLadder from './kbc/PrizeLadder';
import AnswerPanels from './kbc/AnswerPanels';
import {
    LifelinesRow, AudiencePollModal, PhoneAFriendModal, ExpertAdviceModal,
    WalkAwayConfirmModal, LockInConfirmModal, EndGameModal, ExplanationModal, TopicPickerModal,
} from './kbc/KBCModals';
import { Volume2, VolumeX, Mic, MicOff, LogOut, Maximize2 } from 'lucide-react';

// ---- Audio engine — WebAudio synth stings with KBC drama + tick-tock + voice (TTS) ----
// All cues are layered oscillator beeps + noise. No external assets. Voice cues use the
// browser SpeechSynthesis API and fall back silently on unsupported browsers.
function useAudio(muted, mutedRef, voiceMuted, voiceMutedRef) {
    const ctxRef = useRef(null);
    const tickToggleRef = useRef(false);
    const get = () => {
        if (ctxRef.current) return ctxRef.current;
        try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch { /* no-op */ }
        return ctxRef.current;
    };
    const isMuted = () => (mutedRef?.current ?? muted);
    const isVoiceMuted = () => (voiceMutedRef?.current ?? voiceMuted);

    const playSound = useCallback((kind) => {
        if (isMuted()) return;
        const ctx = get();
        if (!ctx) return;
        const t = ctx.currentTime;
        const beep = (freq, dur, type = 'sine', vol = 0.18, delay = 0) => {
            const o = ctx.createOscillator(); const g = ctx.createGain();
            o.type = type; o.frequency.value = freq;
            g.gain.setValueAtTime(0.0001, t + delay);
            g.gain.exponentialRampToValueAtTime(vol, t + delay + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, t + delay + dur);
            o.connect(g); g.connect(ctx.destination);
            o.start(t + delay); o.stop(t + delay + dur + 0.02);
        };
        const sweep = (fromF, toF, dur, type = 'sine', vol = 0.18, delay = 0) => {
            const o = ctx.createOscillator(); const g = ctx.createGain();
            o.type = type;
            o.frequency.setValueAtTime(fromF, t + delay);
            o.frequency.exponentialRampToValueAtTime(toF, t + delay + dur);
            g.gain.setValueAtTime(0.0001, t + delay);
            g.gain.exponentialRampToValueAtTime(vol, t + delay + 0.04);
            g.gain.exponentialRampToValueAtTime(0.0001, t + delay + dur);
            o.connect(g); g.connect(ctx.destination);
            o.start(t + delay); o.stop(t + delay + dur + 0.02);
        };
        const noise = (dur, vol = 0.12, delay = 0, hp = 1200) => {
            const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
            const data = buf.getChannelData(0);
            for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
            const src = ctx.createBufferSource(); src.buffer = buf;
            const filt = ctx.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = hp;
            const g = ctx.createGain();
            g.gain.setValueAtTime(0.0001, t + delay);
            g.gain.exponentialRampToValueAtTime(vol, t + delay + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, t + delay + dur);
            src.connect(filt); filt.connect(g); g.connect(ctx.destination);
            src.start(t + delay); src.stop(t + delay + dur);
        };

        switch (kind) {
            // --- Question entry (dramatic KBC theme-style sting) ---
            case 'question_enter': {
                // Low bass thud + rising orchestral hit + cymbal-style noise wash
                beep(55, 0.55, 'sawtooth', 0.32);
                beep(110, 0.55, 'sawtooth', 0.22, 0.02);
                sweep(220, 660, 0.65, 'triangle', 0.22, 0.05);
                sweep(330, 990, 0.6, 'sine', 0.18, 0.08);
                noise(0.45, 0.10, 0.0, 2400);
                // Settling bass note
                beep(82, 0.65, 'sawtooth', 0.16, 0.5);
                break;
            }
            case 'option_pop': beep(620 + Math.random() * 40, 0.10, 'triangle', 0.13); break;
            case 'pick':       beep(740, 0.10, 'triangle', 0.16); beep(990, 0.08, 'triangle', 0.12, 0.04); break;
            // --- Lock & suspense (the iconic "lock kiya jaye" build) ---
            case 'lock':       beep(220, 0.20, 'sawtooth', 0.24); beep(140, 0.42, 'sawtooth', 0.20, 0.08); noise(0.20, 0.06, 0.0, 2000); break;
            case 'suspense': {
                // Descending sustained tones (a la KBC dramatic pause)
                const steps = [220, 196, 175, 156, 139];
                steps.forEach((f, i) => beep(f, 0.32, 'sawtooth', 0.20, i * 0.26));
                // Pulse heartbeat under it
                for (let i = 0; i < 5; i++) { beep(70, 0.12, 'sine', 0.28, i * 0.26); beep(70, 0.12, 'sine', 0.22, i * 0.26 + 0.13); }
                break;
            }
            case 'correct': {
                // Triumph stinger
                [523, 659, 784, 1047].forEach((f, i) => beep(f, 0.28, 'triangle', 0.24, i * 0.10));
                sweep(523, 1568, 0.7, 'triangle', 0.18, 0.36);
                break;
            }
            case 'wrong': {
                // Dramatic "wrong" thud + descending whomp
                beep(160, 0.45, 'sawtooth', 0.30);
                sweep(220, 70, 0.55, 'sawtooth', 0.25, 0.05);
                noise(0.4, 0.10, 0.0, 600);
                break;
            }
            case 'walkaway':   beep(440, 0.18, 'sine', 0.18); beep(587, 0.20, 'sine', 0.18, 0.10); beep(784, 0.36, 'sine', 0.22, 0.22); break;
            case 'win': {
                // Full ascending fanfare + sweep cymbal
                [523, 659, 784, 1047, 1319, 1568, 2093].forEach((f, i) => beep(f, 0.32, 'triangle', 0.26, i * 0.13));
                noise(0.8, 0.10, 0.0, 3000);
                break;
            }
            case 'lifeline':   sweep(660, 1320, 0.32, 'triangle', 0.18); break;
            case 'fifty':      sweep(1320, 660, 0.20, 'square', 0.18); sweep(1100, 550, 0.20, 'square', 0.18, 0.10); break;
            case 'phone_ring': {
                // Two-tone phone bell ring
                for (let i = 0; i < 2; i++) {
                    const base = i * 0.55;
                    beep(1400, 0.12, 'square', 0.18, base);
                    beep(1100, 0.12, 'square', 0.18, base + 0.13);
                    beep(1400, 0.12, 'square', 0.18, base + 0.26);
                    beep(1100, 0.12, 'square', 0.18, base + 0.39);
                }
                break;
            }
            case 'audience_clap': {
                // Synth applause = filtered white noise bursts in waves
                for (let i = 0; i < 7; i++) {
                    noise(0.18 + Math.random() * 0.12, 0.10 + Math.random() * 0.06, i * 0.09, 1800 + Math.random() * 1200);
                }
                beep(440, 0.25, 'triangle', 0.10, 0.7);
                break;
            }
            case 'expert_gong': {
                // Low gong-like resonant tone
                beep(110, 1.2, 'sine', 0.28);
                beep(165, 1.2, 'sine', 0.20, 0.02);
                beep(220, 1.1, 'sine', 0.14, 0.03);
                noise(0.8, 0.06, 0.0, 800);
                break;
            }
            case 'milestone': {
                // Coin-drop chime
                [880, 1320, 1760].forEach((f, i) => beep(f, 0.28, 'triangle', 0.24, i * 0.12));
                noise(0.30, 0.06, 0.0, 3000);
                break;
            }
            case 'tick_tock': {
                // Alternate tick (high) and tock (low) on each call
                tickToggleRef.current = !tickToggleRef.current;
                if (tickToggleRef.current) {
                    beep(1800, 0.05, 'square', 0.16); // tick
                    noise(0.04, 0.05, 0.0, 3000);
                } else {
                    beep(900, 0.08, 'square', 0.18);  // tock
                    noise(0.05, 0.04, 0.0, 1500);
                }
                break;
            }
            case 'time_warning': {
                // 3 quick urgent beeps when timer hits last quarter
                for (let i = 0; i < 3; i++) beep(880, 0.10, 'square', 0.22, i * 0.14);
                break;
            }
            default: break;
        }
    }, [muted]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Voice (TTS) — Bachchan-style baritone: pick the deepest male voice on the user's
    // system, drop pitch hard, slow rate. Queue utterances so phrases never overlap.
    //
    // NOTE: Amitabh Bachchan's actual voice cannot be used (copyright / personality rights).
    // This is the closest legal approximation using the browser's built-in TTS engine.
    const voiceQueueRef = useRef([]);
    const speakingRef = useRef(false);
    const chosenVoiceRef = useRef(null);

    // Pick best baritone voice on mount (and again when voices load asynchronously).
    useEffect(() => {
        if (typeof window === 'undefined' || !window.speechSynthesis) return;
        const pickVoice = () => {
            const voices = window.speechSynthesis.getVoices() || [];
            if (!voices.length) return;
            // Priority: en-IN male > en-GB male/Daniel > en-US male/David > any male > first
            const score = (v) => {
                let s = 0;
                const n = (v.name || '').toLowerCase();
                const l = (v.lang || '').toLowerCase();
                if (l.startsWith('en-in')) s += 40;
                if (l.startsWith('en-gb')) s += 30;
                if (l.startsWith('en-au')) s += 25;
                if (l.startsWith('en-us')) s += 20;
                if (l.startsWith('en')) s += 10;
                // Strong cues for male voices
                if (/male\b/.test(n)) s += 50;
                if (/(daniel|arthur|david|fred|alex|james|oliver|aaron|brian|george|rishi|ravi|vinay)/.test(n)) s += 35;
                // Penalise female-hint names
                if (/(female|samantha|victoria|karen|moira|tessa|veena|priya|aisha)/.test(n)) s -= 50;
                return s;
            };
            const sorted = [...voices].sort((a, b) => score(b) - score(a));
            chosenVoiceRef.current = sorted[0] || null;
        };
        pickVoice();
        window.speechSynthesis.onvoiceschanged = pickVoice;
        return () => { window.speechSynthesis.onvoiceschanged = null; };
    }, []);

    const pumpQueue = useCallback(() => {
        if (typeof window === 'undefined' || !window.speechSynthesis) return;
        if (speakingRef.current) return;
        if (isVoiceMuted()) {
            // Drop everything queued if voice is muted
            voiceQueueRef.current = [];
            return;
        }
        const next = voiceQueueRef.current.shift();
        if (!next) return;
        try {
            const u = new SpeechSynthesisUtterance(next.text);
            u.lang = next.lang || 'en-IN';
            // Bachchan-style baritone configuration: deep pitch + slow rate.
            u.rate = next.rate ?? 0.88;
            u.pitch = next.pitch ?? 0.55;
            u.volume = next.volume ?? 1.0;
            if (chosenVoiceRef.current) u.voice = chosenVoiceRef.current;
            speakingRef.current = true;
            u.onend = () => {
                speakingRef.current = false;
                // Small breath between utterances so back-to-back lines feel natural.
                setTimeout(pumpQueue, 220);
            };
            u.onerror = () => {
                speakingRef.current = false;
                setTimeout(pumpQueue, 220);
            };
            window.speechSynthesis.speak(u);
        } catch {
            speakingRef.current = false;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const clearSpeech = useCallback(() => {
        voiceQueueRef.current = [];
        speakingRef.current = false;
        if (typeof window !== 'undefined' && window.speechSynthesis) {
            try { window.speechSynthesis.cancel(); } catch { /* no-op */ }
        }
    }, []);

    const speak = useCallback((text, opts = {}) => {
        if (isVoiceMuted()) return;
        if (!text) return;
        // If `replace` is true, cancel pending queue and current utterance (used for events
        // that supersede earlier announcements — e.g. ending the game).
        if (opts.replace) clearSpeech();
        voiceQueueRef.current.push({ text, ...opts });
        pumpQueue();
    }, [pumpQueue, clearSpeech]); // eslint-disable-line react-hooks/exhaustive-deps

    return { playSound, speak, clearSpeech };
}

// ---- Main game wrapper that loads the question bank then renders the stage ----
export default function KBCLearning({ game, onPlayed }) {
    const [stage, setStage] = useState('topic'); // topic | playing
    const [topic, setTopic] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loadingQs, setLoadingQs] = useState(false);

    const fallback = useMemo(() => {
        const seeded = game?.config?.fallback_questions || [];
        const bank = seeded.length >= 16 ? seeded : FALLBACK_BANK;
        return bank.slice(0, 16);
    }, [game]);

    const beginWithTopic = useCallback(async (chosenTopic) => {
        setTopic(chosenTopic);
        setStage('playing');
        // Start with fallback so the hot seat is INSTANT — swap in AI bank when it lands.
        setQuestions(fallback);
        setLoadingQs(true);
        try {
            const r = await api.post('/games/kbc/generate-questions', { topic: chosenTopic, num_questions: 16 });
            const fresh = (r.data?.questions || []).filter((q) => q && q.options?.length === 4);
            if (fresh.length >= 12) {
                // Pad to 16 if AI returned 12-15
                const padded = fresh.length >= 16 ? fresh.slice(0, 16) : [...fresh, ...fallback.slice(fresh.length, 16)];
                setQuestions(padded);
                toast.success(`✨ Live AI-tuned questions ready (${padded.length})`);
            }
        } catch {
            toast.info('Using curated cables/wires question bank.');
        } finally {
            setLoadingQs(false);
        }
    }, [fallback]);

    const beginCurated = useCallback(() => {
        setTopic('curated cables/wires bank');
        setQuestions(fallback);
        setStage('playing');
    }, [fallback]);

    if (stage === 'topic') {
        return (
            <TopicPickerModal
                presets={TOPIC_PRESETS}
                onPick={beginWithTopic}
                onClose={beginCurated}
            />
        );
    }

    return (
        <KBCStage
            game={game}
            onPlayed={onPlayed}
            questions={questions}
            topic={topic}
            loadingQs={loadingQs}
            onReturnHome={() => { setStage('topic'); setQuestions([]); }}
        />
    );
}

// ---- The actual stage ----
function KBCStage({ game, onPlayed, questions, topic, loadingQs, onReturnHome }) {
    const [idx, setIdx] = useState(0);             // index of current question (0..15)
    const [lockedIdx, setLockedIdx] = useState(-1); // last question CORRECTLY answered
    const [selected, setSelected] = useState(null);
    const [lockedAnswer, setLockedAnswer] = useState(null);   // null = not locked yet
    const [outcome, setOutcome] = useState(null);   // null | 'won' | 'walked' | 'lost'
    const [finalPoints, setFinalPoints] = useState(0);

    const [secondsLeft, setSecondsLeft] = useState(QUESTION_SECONDS);
    const [muted, setMuted] = useState(false);
    const [voiceMuted, setVoiceMuted] = useState(false);
    const mutedRef = useRef(false);
    const voiceMutedRef = useRef(false);
    useEffect(() => { mutedRef.current = muted; }, [muted]);
    useEffect(() => { voiceMutedRef.current = voiceMuted; }, [voiceMuted]);
    const { playSound, speak, clearSpeech } = useAudio(muted, mutedRef, voiceMuted, voiceMutedRef);

    // Accumulated points across all correctly-answered questions (incl. speed multiplier).
    const [accumulated, setAccumulated] = useState(0);
    // Per-question award history, used for end-of-game stats & walk-away.
    const [awardHistory, setAwardHistory] = useState([]); // [{idx, base, mult, pts}]
    // Multiplier captured at the moment the user LOCKED (locked at click time for fairness).
    const [lockedMult, setLockedMult] = useState(null);

    const [usedLifelines, setUsedLifelines] = useState([]);
    const [eliminated, setEliminated] = useState([]);
    const [activeModal, setActiveModal] = useState(null);  // 'audience' | 'friend' | 'expert' | 'walkaway' | 'lockin' | 'explanation'
    const [submitted, setSubmitted] = useState(false);

    const containerRef = useRef(null);
    const requestFullscreen = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            el.requestFullscreen?.();
        }
    }, []);

    // Cancel any pending speech if the stage unmounts (e.g. exit to arcade).
    useEffect(() => {
        return () => { clearSpeech(); };
    }, [clearSpeech]);

    // When the user mutes voice, immediately drop in-flight speech.
    useEffect(() => {
        if (voiceMuted) clearSpeech();
    }, [voiceMuted, clearSpeech]);

    const q = questions[idx];

    // Timer — emits tick-tock every second, warning ping at quarter boundaries.
    useEffect(() => {
        if (outcome || activeModal || lockedAnswer !== null) return;
        if (!q) return;
        setSecondsLeft(QUESTION_SECONDS);
        const id = setInterval(() => {
            setSecondsLeft((s) => {
                if (s <= 1) {
                    clearInterval(id);
                    handleTimeout();
                    return 0;
                }
                playSound('tick_tock');
                // Warn at every quarter boundary
                const justEnteredQuarter = SPEED_TIERS.some(
                    (tier) => Math.abs((s - 1) / QUESTION_SECONDS - tier.from) < (1 / QUESTION_SECONDS) / 2,
                );
                if (justEnteredQuarter && (s - 1) > 0) {
                    setTimeout(() => playSound('time_warning'), 40);
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, activeModal, lockedAnswer, outcome, q]);

    // Dramatic question entry — synth sting + TTS preface ("Question for N points")
    const lastSpokenIdxRef = useRef(-1);
    useEffect(() => {
        if (!q || outcome) return;
        if (lastSpokenIdxRef.current === idx) return;
        lastSpokenIdxRef.current = idx;
        playSound('question_enter');
        // Sequence option-pop sounds for visual reveal
        for (let i = 0; i < 4; i++) {
            setTimeout(() => playSound('option_pop'), 600 + i * 220);
        }
        // Voice intro — dramatic baritone delivery. Queued, so it auto-chains with anything pending.
        const points = LADDER[idx].toLocaleString();
        const isJackpot = idx === JACKPOT_INDEX;
        const intro = isJackpot
            ? `And now... the jackpot question. For ${points} points.`
            : SAFE_INDICES.includes(idx)
                ? `Safe milestone question. For ${points} points.`
                : `Question number ${idx + 1}. For ${points} points.`;
        // Delay so the synth sting "lands" first before voice begins.
        setTimeout(() => speak(intro, { rate: 0.82, pitch: 0.52 }), 850);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, q, outcome]);

    // Reset per-question state on idx change. Clear any leftover speech from prev question.
    useEffect(() => {
        setSelected(null);
        setLockedAnswer(null);
        setEliminated([]);
        setLockedMult(null);
        clearSpeech();
    }, [idx, clearSpeech]);

    const handleTimeout = useCallback(() => {
        setLockedAnswer(-1);   // signal an empty timeout answer
        playSound('wrong');
        speak('Samay samaapt. Time is up!', { replace: true });
        setTimeout(() => endLost(idx), 1400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx]);

    const handlePick = useCallback((i) => {
        if (lockedAnswer !== null || outcome) return;
        playSound('pick');
        setSelected(i);
        // Capture the speed multiplier at the moment of CLICK (so the suspense pause
        // doesn't penalise the player). Frozen until confirm/cancel.
        setLockedMult(speedMultiplier(secondsLeft));
        setActiveModal('lockin');
    }, [lockedAnswer, outcome, playSound, secondsLeft]);

    const confirmLock = useCallback(() => {
        setActiveModal(null);
        if (selected === null) return;
        playSound('lock');
        // "Computer ji, lock kiya jaaye" (~2.2s in baritone) — queued first.
        speak('Computer ji. Lock. Kiya. Jaaye.', { rate: 0.78, pitch: 0.5 });
        playSound('suspense');
        // Suspense reveal is timed to land AFTER the lock-phrase finishes naturally.
        // (lock-phrase ~2.0-2.4s @ rate 0.78 → reveal at 2.6s) The "Sahi/Galat jawaab"
        // will queue behind any still-playing lock utterance — never overlaps.
        setTimeout(() => {
            const correct = selected === q.correct_index;
            setLockedAnswer(selected);
            const mult = lockedMult ?? 1;
            if (correct) {
                const base = LADDER[idx];
                const award = base * mult;
                setAccumulated((a) => a + award);
                setAwardHistory((h) => [...h, { idx, base, mult, pts: award }]);
                playSound('correct');
                speak(mult >= 3 ? 'Sahi jawaab! Bahut khoob.' : 'Sahi jawaab!');
                if (SAFE_INDICES.includes(idx)) {
                    setTimeout(() => playSound('milestone'), 600);
                }
                setLockedIdx(idx);
                setTimeout(() => setActiveModal('explanation'), 2400);
            } else {
                playSound('wrong');
                speak('Galat jawaab.');
                setTimeout(() => setActiveModal('explanation'), 2400);
            }
        }, 2600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, q, idx, playSound, speak, lockedMult]);

    const cancelLock = useCallback(() => {
        setActiveModal(null);
        setSelected(null);
    }, []);

    const advance = useCallback(() => {
        setActiveModal(null);
        if (lockedAnswer === null) return;
        const wasCorrect = lockedAnswer === q.correct_index;
        if (!wasCorrect) {
            endLost(idx);
            return;
        }
        if (idx >= JACKPOT_INDEX) {
            endWon();
            return;
        }
        setIdx((i) => i + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lockedAnswer, q, idx]);

    const endLost = useCallback((atIdx) => {
        // Drop to safe milestone floor (no multipliers honored below milestone) — but if the
        // player has accumulated MORE than the milestone (e.g. they answered fast and the
        // milestone is small), they still get to keep the milestone amount as the safety net.
        const safeFloor = fallbackPointsOnWrong(atIdx);
        setFinalPoints(safeFloor);
        setOutcome('lost');
        setActiveModal(null);
    }, []);

    const endWon = useCallback(() => {
        // Player cleared all 16 — take everything accumulated (includes the jackpot question).
        setFinalPoints((prev) => Math.max(prev, accumulated));
        setOutcome('won');
        playSound('win');
        speak('Aap jeet gaye! You are the new Crorepati!', { replace: true, rate: 0.8, pitch: 0.5 });
    }, [playSound, speak, accumulated]);

    const endWalkAway = useCallback(() => {
        // Walking away takes EVERYTHING accumulated so far (speed-bonus stays with you).
        setFinalPoints(accumulated);
        setOutcome('walked');
        playSound('walkaway');
        speak('Aapne game chhod diya. Well played.', { replace: true, rate: 0.85 });
        setActiveModal(null);
    }, [accumulated, playSound, speak]);

    // ---- Lifelines ----
    const useLifeline = useCallback((id) => {
        if (usedLifelines.includes(id)) return;
        if (lockedAnswer !== null) return;
        setUsedLifelines((u) => [...u, id]);
        if (id === 'fifty_fifty') {
            playSound('fifty');
            // No voice on 50:50 — sound + visual reveal is enough; voice would clutter quick UX.
            setEliminated(pickFiftyFiftyHidden(q.correct_index));
            return;
        }
        if (id === 'audience_poll') { playSound('audience_clap'); setActiveModal('audience'); return; }
        if (id === 'phone_a_friend') { playSound('phone_ring'); setActiveModal('friend'); return; }
        if (id === 'expert_advice') { playSound('expert_gong'); setActiveModal('expert'); return; }
        playSound('lifeline');
    }, [usedLifelines, lockedAnswer, q, playSound, speak]);

    // ---- Submit score on game end ----
    useEffect(() => {
        if (outcome && !submitted) {
            setSubmitted(true);
            api.post(`/games/${game.code}/submit`, {
                score: finalPoints,
                meta: { topic, outcome, level_reached: lockedIdx + 1 },
            }).then(() => { onPlayed?.(); }).catch(() => { /* swallow */ });
        }
    }, [outcome, submitted, finalPoints, topic, lockedIdx, game, onPlayed]);

    const availableLifelines = LIFELINES.filter((l) => !usedLifelines.includes(l));
    const lastSafeAmount = accumulated;
    const currentTier = speedTier(secondsLeft);

    const handlePlayAgain = () => {
        setOutcome(null);
        setSubmitted(false);
        setIdx(0);
        setLockedIdx(-1);
        setSelected(null);
        setLockedAnswer(null);
        setEliminated([]);
        setUsedLifelines([]);
        setActiveModal(null);
        setFinalPoints(0);
        setAccumulated(0);
        setAwardHistory([]);
        setLockedMult(null);
        lastSpokenIdxRef.current = -1;
        onReturnHome();
    };

    if (!q) {
        return (
            <div className="rounded-2xl bg-slate-900 p-6 text-center text-slate-400 text-sm">
                Loading hot seat…
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="relative rounded-3xl overflow-hidden ring-1 ring-amber-400/30"
            style={{
                background: 'radial-gradient(ellipse at top, #1e1b4b 0%, #0f172a 35%, #020617 100%)',
                minHeight: '700px',
                boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
            }}
            data-testid="kbc-stage"
        >
            {/* Ambient bokeh */}
            <div aria-hidden className="absolute inset-0 pointer-events-none opacity-40">
                <div className="absolute -top-10 left-10 w-72 h-72 rounded-full bg-amber-400/30 blur-3xl" />
                <div className="absolute top-1/3 -right-10 w-72 h-72 rounded-full bg-fuchsia-500/20 blur-3xl" />
                <div className="absolute bottom-10 left-1/3 w-72 h-72 rounded-full bg-violet-500/20 blur-3xl" />
            </div>

            {/* Top bar */}
            <div className="relative z-10 flex items-center justify-between gap-2 px-3 sm:px-5 pt-3 sm:pt-4">
                <div className="flex items-center gap-2">
                    <div className="hidden sm:block">
                        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Question</div>
                        <div className="font-display font-extrabold text-white text-lg" data-testid="kbc-question-number">
                            Q{idx + 1} <span className="opacity-50 text-sm">/ 16</span>
                        </div>
                    </div>
                    <div className="sm:hidden">
                        <div className="font-display font-extrabold text-white text-sm" data-testid="kbc-question-number">
                            Q{idx + 1}<span className="opacity-50 text-xs">/16</span>
                        </div>
                    </div>
                </div>

                {/* Center: big segmented timer with multiplier */}
                <div className="flex flex-col items-center -mt-1">
                    <TimerRing seconds={secondsLeft} total={QUESTION_SECONDS} paused={!!activeModal || lockedAnswer !== null} />
                </div>

                <div className="flex flex-col items-end gap-1.5">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <button onClick={() => setMuted((m) => !m)}
                            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-800/70 hover:bg-slate-700 grid place-items-center text-amber-300 ring-1 ring-amber-400/30"
                            data-testid="kbc-mute-btn" title={muted ? 'Unmute sound effects' : 'Mute sound effects'}>
                            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setVoiceMuted((m) => !m)}
                            className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full ${voiceMuted ? 'bg-slate-800/70' : 'bg-amber-500/20'} hover:bg-slate-700 grid place-items-center text-amber-300 ring-1 ring-amber-400/30`}
                            data-testid="kbc-voice-btn" title={voiceMuted ? 'Turn voice on (Bachchan-style commentary)' : 'Turn voice off'}>
                            {voiceMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                        </button>
                        <button onClick={requestFullscreen}
                            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-800/70 hover:bg-slate-700 grid place-items-center text-amber-300 ring-1 ring-amber-400/30"
                            data-testid="kbc-fullscreen-btn" title="Fullscreen">
                            <Maximize2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setActiveModal('walkaway')}
                            disabled={lockedAnswer !== null || activeModal}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-rose-500 to-rose-700 text-white font-display font-extrabold text-xs disabled:opacity-40 disabled:cursor-not-allowed shadow-lg hover:shadow-xl active:scale-95 transition"
                            data-testid="kbc-walkaway-btn">
                            <LogOut className="w-3.5 h-3.5" /> Quit
                        </button>
                    </div>
                    <div className="text-right pr-1" data-testid="kbc-accumulated">
                        <div className="text-[9px] uppercase tracking-widest text-amber-300/80 font-bold">Bank</div>
                        <div className="font-display font-extrabold text-amber-300 text-base sm:text-lg leading-tight"
                            style={{ textShadow: '0 0 10px rgba(251,191,36,0.45)' }}>
                            {accumulated.toLocaleString()}
                            <span className="text-[10px] sm:text-xs text-amber-200/70 ml-1">pts</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative z-10 mt-2 px-3 sm:px-5 pb-5 grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-4">
                {/* LEFT: Question + answers + lifelines */}
                <div className="space-y-3 sm:space-y-4">
                    {/* Topic chip + AI status */}
                    <div className="flex items-center gap-2 text-[10px] sm:text-xs">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/30 font-bold tracking-wider uppercase">
                            🎯 {topic?.slice(0, 36)}{topic && topic.length > 36 ? '…' : ''}
                        </span>
                        {loadingQs && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-violet-500/20 text-violet-200 ring-1 ring-violet-400/30 font-bold tracking-wider uppercase animate-pulse">
                                ✨ AI tuning…
                            </span>
                        )}
                    </div>

                    {/* Question card */}
                    <motion.div
                        key={`q-${idx}`}
                        initial={{ opacity: 0, y: -20, scale: 0.94 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.65, ease: [0.22, 1.4, 0.36, 1] }}
                        className="relative rounded-2xl px-4 sm:px-6 py-4 sm:py-5 text-center overflow-hidden"
                        style={{
                            background: 'linear-gradient(135deg, #0a1a4a 0%, #06112e 60%, #020617 100%)',
                            border: '2px solid rgba(251,191,36,0.45)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.07), 0 0 28px rgba(251,191,36,0.18)',
                        }}
                        data-testid="kbc-question-card"
                    >
                        {/* Glow sweep on entry */}
                        <motion.div
                            key={`glow-${idx}`}
                            initial={{ x: '-120%' }}
                            animate={{ x: '120%' }}
                            transition={{ duration: 1.0, ease: 'easeOut' }}
                            className="absolute inset-y-0 w-1/3 pointer-events-none"
                            style={{ background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.20), transparent)' }}
                        />
                        <div className="absolute top-2 left-4 text-[9px] uppercase tracking-widest text-amber-300 font-bold">
                            For {LADDER[idx].toLocaleString()} pts × <span style={{ color: currentTier.color }}>{currentTier.mult}x</span>
                        </div>
                        <div className="absolute top-2 right-4 text-[9px] uppercase tracking-widest text-amber-300 font-bold">
                            {idx === JACKPOT_INDEX ? 'JACKPOT' : SAFE_INDICES.includes(idx) ? 'SAFE MILESTONE' : ''}
                        </div>
                        <p className="font-display font-bold text-white text-base sm:text-lg leading-snug mt-4" data-testid="kbc-question-text">
                            {q.question}
                        </p>
                    </motion.div>

                    {/* Answer panels */}
                    <AnswerPanels
                        options={q.options}
                        selectedIndex={selected}
                        lockedIndex={lockedAnswer}
                        correctIndex={lockedAnswer !== null ? q.correct_index : null}
                        eliminatedIndices={eliminated}
                        disabled={!!activeModal || lockedAnswer !== null}
                        onSelect={handlePick}
                    />

                    {/* Lifelines */}
                    <div className="pt-1">
                        <LifelinesRow
                            available={availableLifelines}
                            onUse={useLifeline}
                            disabled={lockedAnswer !== null || !!activeModal}
                        />
                    </div>

                    {/* Walk-away hint */}
                    {accumulated > 0 && lockedAnswer === null && (
                        <div className="text-center text-[10px] sm:text-xs text-amber-200/80">
                            Walk away now and take <b className="text-amber-300">{accumulated.toLocaleString()} pts</b>.
                        </div>
                    )}
                </div>

                {/* RIGHT: Prize ladder */}
                <div className="order-first lg:order-last">
                    <PrizeLadder currentIndex={idx} lockedIndex={lockedIdx} lostAt={outcome === 'lost' ? idx : null} />
                </div>
            </div>

            {/* Modals */}
            <AnimatePresence>
                {activeModal === 'lockin' && selected !== null && (
                    <LockInConfirmModal
                        letter={OPTION_LETTERS[selected]}
                        text={q.options[selected]}
                        multiplier={lockedMult}
                        award={LADDER[idx] * (lockedMult || 1)}
                        onConfirm={confirmLock}
                        onCancel={cancelLock}
                    />
                )}
                {activeModal === 'audience' && (
                    <AudiencePollModal poll={q.audience_poll || [25, 25, 25, 25]} options={q.options} onClose={() => setActiveModal(null)} />
                )}
                {activeModal === 'friend' && (
                    <PhoneAFriendModal hint={q.friend_hint || `I'd go with option ${OPTION_LETTERS[q.correct_index]}, I'm pretty sure.`} onClose={() => setActiveModal(null)} />
                )}
                {activeModal === 'expert' && (
                    <ExpertAdviceModal insight={q.expert_insight || `Based on industry practice, option ${OPTION_LETTERS[q.correct_index]} is correct.`} onClose={() => setActiveModal(null)} />
                )}
                {activeModal === 'walkaway' && (
                    <WalkAwayConfirmModal
                        amount={accumulated}
                        onConfirm={endWalkAway}
                        onCancel={() => setActiveModal(null)}
                    />
                )}
                {activeModal === 'explanation' && lockedAnswer !== null && (
                    <ExplanationModal
                        correct={lockedAnswer === q.correct_index}
                        explanation={q.explanation || (lockedAnswer === q.correct_index ? 'Spot on!' : `Correct answer was option ${OPTION_LETTERS[q.correct_index]}.`)}
                        onNext={advance}
                        isLast={idx >= JACKPOT_INDEX}
                    />
                )}
                {outcome && (
                    <EndGameModal
                        outcome={outcome}
                        points={finalPoints}
                        levelReached={lockedIdx + 1}
                        onPlayAgain={handlePlayAgain}
                        onExit={() => { handlePlayAgain(); }}
                    />
                )}
            </AnimatePresence>

            {/* Confetti on win */}
            {outcome === 'won' && <ConfettiBurst />}
        </div>
    );
}

// ---- Big Segmented Timer Ring with 4 colored quarters (4x/3x/2x/1x speed bonus) ----
function TimerRing({ seconds, total, paused }) {
    // Outer ring is split into 4 quadrants. As the timer counts down, each quadrant
    // dims from "active glow" to faded grey when its time-window passes.
    // Center shows: number + multiplier label + small "TIME" caption.
    const tier = speedTier(seconds, total);
    const pct = Math.max(0, seconds) / total;
    const danger = pct <= 0.25 && !paused;
    // Use SVG with 4 arcs.
    const radius = 56;
    const stroke = 9;
    const cx = 70, cy = 70;
    const circ = 2 * Math.PI * radius;
    const segLen = circ / 4 - 4;  // small gap between segments

    // For each quadrant (top, right, bottom, left), the corresponding speed tier in order
    // from highest-mult to lowest:
    //   top    = first quarter (4x)
    //   right  = 3x
    //   bottom = 2x
    //   left   = 1x
    // We draw each segment, lit if pct is currently within its window, dim otherwise.
    const segments = [
        { rotate: -90, tier: SPEED_TIERS[0] },   // top
        { rotate: 0,   tier: SPEED_TIERS[1] },   // right
        { rotate: 90,  tier: SPEED_TIERS[2] },   // bottom
        { rotate: 180, tier: SPEED_TIERS[3] },   // left
    ];

    return (
        <div className="relative" style={{ width: 140, height: 140 }} data-testid="kbc-timer">
            <svg viewBox="0 0 140 140" className="w-full h-full">
                {/* Background plate */}
                <defs>
                    <radialGradient id="timer-bg" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#0f172a" />
                        <stop offset="100%" stopColor="#020617" />
                    </radialGradient>
                </defs>
                <circle cx={cx} cy={cy} r={radius - 5} fill="url(#timer-bg)" stroke="#1e293b" strokeWidth="2" />
                {/* 4 segments */}
                {segments.map(({ rotate, tier: segTier }, i) => {
                    const isCurrent = tier.mult === segTier.mult && !paused;
                    const isPast = segTier.mult > tier.mult;     // higher-mult tiers expire first
                    const fillColor = isCurrent ? segTier.color : (isPast ? '#334155' : segTier.color);
                    const opacity = isCurrent ? 1 : (isPast ? 0.25 : 0.45);
                    return (
                        <g key={i} transform={`rotate(${rotate} ${cx} ${cy})`}>
                            <circle
                                cx={cx}
                                cy={cy}
                                r={radius}
                                fill="none"
                                stroke={fillColor}
                                strokeWidth={stroke}
                                strokeLinecap="round"
                                strokeDasharray={`${segLen} ${circ}`}
                                strokeDashoffset={-2}
                                opacity={opacity}
                                style={{
                                    filter: isCurrent ? `drop-shadow(0 0 10px ${fillColor})` : undefined,
                                    transition: 'opacity 0.4s, stroke 0.4s',
                                }}
                                transform={`rotate(-${segLen / 2 / radius * 180 / Math.PI} ${cx} ${cy})`}
                            />
                        </g>
                    );
                })}
                {/* Inner active progress arc — same color as current tier, shrinks within current quadrant */}
                {/* Quadrant fill amount */}
                <circle
                    cx={cx} cy={cy} r={radius - 14}
                    fill="none"
                    stroke={paused ? '#475569' : tier.color}
                    strokeWidth="3"
                    strokeDasharray={2 * Math.PI * (radius - 14)}
                    strokeDashoffset={2 * Math.PI * (radius - 14) * (1 - pct)}
                    transform={`rotate(-90 ${cx} ${cy})`}
                    style={{
                        filter: danger ? 'drop-shadow(0 0 8px #ef4444)' : `drop-shadow(0 0 5px ${tier.color})`,
                        transition: 'stroke 0.4s',
                    }}
                />
            </svg>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <motion.div
                    key={tier.mult}
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4 }}
                    className="font-display font-extrabold text-[10px] uppercase tracking-widest"
                    style={{ color: tier.color, textShadow: `0 0 8px ${tier.color}` }}
                >
                    {tier.mult}x · {tier.label}
                </motion.div>
                <div className="font-display font-extrabold text-white leading-none mt-1" style={{ fontSize: '36px' }}>
                    {paused ? '⏸' : seconds}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">seconds</div>
            </div>
        </div>
    );
}

// ---- Confetti ----
function ConfettiBurst() {
    const pieces = useMemo(() => Array.from({ length: 80 }).map((_, i) => ({
        id: i,
        color: ['#fbbf24', '#ef4444', '#3b82f6', '#10b981', '#f43f5e', '#a855f7'][i % 6],
        left: Math.random() * 100,
        delay: Math.random() * 0.4,
        rot: Math.random() * 360,
    })), []);
    return (
        <div className="pointer-events-none absolute inset-0 z-40 overflow-hidden">
            {pieces.map((p) => (
                <motion.div
                    key={p.id}
                    initial={{ y: -30, x: 0, opacity: 0, rotate: p.rot }}
                    animate={{ y: '110vh', opacity: [0, 1, 1, 0], rotate: p.rot + 720 }}
                    transition={{ duration: 3 + Math.random() * 2, delay: p.delay, ease: 'easeIn' }}
                    className="absolute w-2.5 h-3.5"
                    style={{ left: `${p.left}%`, background: p.color, borderRadius: 2 }}
                />
            ))}
        </div>
    );
}
